/**
 * HelpMarkdown — a small, dependency-free Markdown renderer for the in-app
 * Help page. It renders the subset of GitHub-Flavored Markdown the user guide
 * uses (headings, paragraphs, bold/italic/inline-code/links, fenced code,
 * ordered/unordered lists with nesting, GFM tables, blockquotes, horizontal
 * rules) into MUI components so it inherits the active theme.
 *
 * We render our OWN docs (served from the backend), so the input is trusted —
 * but we still never use dangerouslySetInnerHTML: everything becomes React
 * nodes, so stray HTML in the source shows as text rather than executing.
 */
import React from 'react';
import { Box, Typography, Divider, Link, useTheme } from '@mui/material';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { useNavigate } from 'react-router-dom';
import { MONO_FONT } from '@/shared/typography';

// ── Inline formatting ────────────────────────────────────────────────────────

interface InlineToken {
  type: 'code' | 'link' | 'bold' | 'italic';
  index: number;
  length: number;
  content: string;
  href?: string;
}

const INLINE_PATTERNS: Array<{ type: InlineToken['type']; re: RegExp }> = [
  { type: 'code', re: /`([^`]+)`/ },
  { type: 'link', re: /\[([^\]]+)\]\(([^)\s]+)\)/ },
  { type: 'bold', re: /\*\*([^*]+)\*\*/ },
  { type: 'italic', re: /\*([^*\n]+)\*|_([^_\n]+)_/ },
];

const InlineCode: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <Box
      component="code"
      sx={{
        fontFamily: MONO_FONT,
        fontSize: '0.85em',
        px: 0.5,
        py: 0.1,
        borderRadius: '4px',
        bgcolor: theme.palette.action.hover,
        color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
        wordBreak: 'break-word',
      }}
    >
      {children}
    </Box>
  );
};

const InlineLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => {
  const navigate = useNavigate();
  // In-guide cross links look like "09-report-builder.md" (optionally #anchor).
  const guideMatch = /^(\d+)-([a-z0-9-]+)\.md(#.*)?$/i.exec(href.trim());
  if (guideMatch) {
    const slug = guideMatch[2];
    return (
      <Link
        component="button"
        type="button"
        onClick={() => navigate(`/help/${slug}`)}
        underline="hover"
        sx={{ verticalAlign: 'baseline', fontSize: 'inherit', p: 0 }}
      >
        {children}
      </Link>
    );
  }
  const external = /^https?:\/\//i.test(href);
  return (
    <Link
      href={href}
      underline="hover"
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </Link>
  );
};

function findNextToken(text: string): InlineToken | null {
  let best: InlineToken | null = null;
  for (const { type, re } of INLINE_PATTERNS) {
    const m = re.exec(text);
    if (!m) continue;
    const idx = m.index;
    if (best !== null && idx >= best.index) continue;
    if (type === 'link') {
      best = { type, index: idx, length: m[0].length, content: m[1], href: m[2] };
    } else if (type === 'italic') {
      best = { type, index: idx, length: m[0].length, content: m[1] ?? m[2] };
    } else {
      best = { type, index: idx, length: m[0].length, content: m[1] };
    }
  }
  return best;
}

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let remaining = text;
  let cursor = 0;
  // Bound the loop defensively so a pathological input can never hang the UI.
  for (let guard = 0; guard < 5000; guard += 1) {
    const token = findNextToken(remaining);
    if (!token) {
      if (remaining) nodes.push(remaining);
      break;
    }
    if (token.index > 0) nodes.push(remaining.slice(0, token.index));
    const k = `${keyPrefix}-${cursor}`;
    cursor += 1;
    if (token.type === 'code') {
      nodes.push(<InlineCode key={k}>{token.content}</InlineCode>);
    } else if (token.type === 'link') {
      nodes.push(
        <InlineLink key={k} href={token.href!}>
          {renderInline(token.content, `${k}i`)}
        </InlineLink>,
      );
    } else if (token.type === 'bold') {
      nodes.push(
        <Box component="strong" key={k} sx={{ fontWeight: 700 }}>
          {renderInline(token.content, `${k}i`)}
        </Box>,
      );
    } else {
      nodes.push(
        <Box component="em" key={k} sx={{ fontStyle: 'italic' }}>
          {renderInline(token.content, `${k}i`)}
        </Box>,
      );
    }
    remaining = remaining.slice(token.index + token.length);
  }
  return nodes;
}

// ── Block-level parsing ──────────────────────────────────────────────────────

const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const HR_RE = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/;
const LIST_RE = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
const TABLE_SEP_RE = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/;

function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  // Split on unescaped pipes.
  return s.split(/(?<!\\)\|/).map((c) => c.replace(/\\\|/g, '|').trim());
}

interface ListNode {
  ordered: boolean;
  items: Array<{ text: string; children?: ListNode }>;
}

function parseList(lines: string[]): ListNode {
  // Build a (possibly nested) list from a block of list lines using indentation.
  const firstMarker = LIST_RE.exec(lines[0]);
  const root: ListNode = { ordered: firstMarker ? /\d/.test(firstMarker[2]) : false, items: [] };
  const stack: Array<{ indent: number; node: ListNode }> = [{ indent: -1, node: root }];

  for (const raw of lines) {
    const m = LIST_RE.exec(raw);
    if (!m) {
      // Continuation line — append to the deepest last item's text.
      const level = stack[stack.length - 1];
      if (level.node.items.length > 0) {
        const last = level.node.items[level.node.items.length - 1];
        last.text += ` ${raw.trim()}`;
      }
      continue;
    }
    const indent = m[1].length;
    const ordered = /\d/.test(m[2]);
    const text = m[3];

    // Dedent: drop levels more indented than this item.
    while (stack.length > 1 && indent < stack[stack.length - 1].indent) {
      stack.pop();
    }
    let level = stack[stack.length - 1];

    // Indent: nest under the current level's last item when we go deeper.
    if (indent > level.indent && level.node.items.length > 0 && level.indent >= 0) {
      const lastItem = level.node.items[level.node.items.length - 1];
      if (!lastItem.children) lastItem.children = { ordered, items: [] };
      stack.push({ indent, node: lastItem.children });
      level = stack[stack.length - 1];
    }

    level.node.items.push({ text });
    if (level.indent < 0) level.indent = indent; // base level adopts the first item's indent
  }
  return root;
}

const RenderedList: React.FC<{ node: ListNode; k: string }> = ({ node, k }) => {
  const Tag = node.ordered ? 'ol' : 'ul';
  return (
    <Box
      component={Tag as 'ul'}
      sx={{ pl: 3, my: 1, '& li': { mb: 0.5, lineHeight: 1.6 } }}
    >
      {node.items.map((item, i) => (
        <li key={`${k}-${i}`}>
          {renderInline(item.text, `${k}-${i}`)}
          {item.children && <RenderedList node={item.children} k={`${k}-${i}c`} />}
        </li>
      ))}
    </Box>
  );
};

interface Block {
  type: 'heading' | 'code' | 'table' | 'list' | 'quote' | 'hr' | 'paragraph';
  level?: number;
  lang?: string;
  lines: string[];
}

function parseBlocks(markdown: string): Block[] {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Fenced code
    const fence = /^\s*```(.*)$/.exec(line);
    if (fence) {
      const lang = fence[1].trim();
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1; // skip closing fence
      blocks.push({ type: 'code', lang, lines: body });
      continue;
    }

    // Blank
    if (line.trim() === '') {
      i += 1;
      continue;
    }

    // Heading
    const h = HEADING_RE.exec(line);
    if (h) {
      blocks.push({ type: 'heading', level: h[1].length, lines: [h[2].trim()] });
      i += 1;
      continue;
    }

    // Table: current row has a pipe and next line is a separator
    if (line.includes('|') && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1]) && lines[i + 1].includes('-')) {
      const tableLines = [line, lines[i + 1]];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        tableLines.push(lines[i]);
        i += 1;
      }
      blocks.push({ type: 'table', lines: tableLines });
      continue;
    }

    // Horizontal rule
    if (HR_RE.test(line)) {
      blocks.push({ type: 'hr', lines: [] });
      i += 1;
      continue;
    }

    // Blockquote
    if (/^\s*>\s?/.test(line)) {
      const body: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        body.push(lines[i].replace(/^\s*>\s?/, ''));
        i += 1;
      }
      blocks.push({ type: 'quote', lines: body });
      continue;
    }

    // List
    if (LIST_RE.test(line)) {
      const body: string[] = [];
      while (
        i < lines.length &&
        lines[i].trim() !== '' &&
        (LIST_RE.test(lines[i]) || /^\s+\S/.test(lines[i]))
      ) {
        body.push(lines[i]);
        i += 1;
      }
      blocks.push({ type: 'list', lines: body });
      continue;
    }

    // Paragraph — gather until blank / structural line
    const body: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !HEADING_RE.test(lines[i]) &&
      !/^\s*```/.test(lines[i]) &&
      !HR_RE.test(lines[i]) &&
      !/^\s*>\s?/.test(lines[i]) &&
      !LIST_RE.test(lines[i])
    ) {
      body.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: 'paragraph', lines: body });
  }
  return blocks;
}

// ── Public component ─────────────────────────────────────────────────────────

const HEADING_STYLES: Record<number, { fontSize: number; fontWeight: number; mt: number; mb: number }> = {
  1: { fontSize: 28, fontWeight: 700, mt: 0, mb: 2 },
  2: { fontSize: 21, fontWeight: 700, mt: 3.5, mb: 1.25 },
  3: { fontSize: 17, fontWeight: 600, mt: 2.5, mb: 1 },
  4: { fontSize: 15, fontWeight: 600, mt: 2, mb: 0.75 },
  5: { fontSize: 13.5, fontWeight: 600, mt: 1.5, mb: 0.5 },
  6: { fontSize: 12.5, fontWeight: 600, mt: 1.25, mb: 0.5 },
};

export const HelpMarkdown: React.FC<{ markdown: string }> = ({ markdown }) => {
  const theme = useTheme();
  const blocks = React.useMemo(() => parseBlocks(markdown), [markdown]);

  return (
    <Box sx={{ '& > *:first-of-type': { mt: 0 }, fontSize: 14, lineHeight: 1.7, color: theme.palette.text.primary }}>
      {blocks.map((block, idx) => {
        const key = `b-${idx}`;
        switch (block.type) {
          case 'heading': {
            const s = HEADING_STYLES[block.level ?? 2] ?? HEADING_STYLES[2];
            return (
              <Typography
                key={key}
                component={`h${block.level ?? 2}` as 'h2'}
                sx={{
                  fontSize: s.fontSize,
                  fontWeight: s.fontWeight,
                  mt: s.mt,
                  mb: s.mb,
                  letterSpacing: -0.2,
                  lineHeight: 1.3,
                  ...(block.level === 2
                    ? { pb: 0.5, borderBottom: `1px solid ${theme.palette.divider}` }
                    : {}),
                }}
              >
                {renderInline(block.lines[0], key)}
              </Typography>
            );
          }
          case 'code':
            return (
              <Box
                key={key}
                component="pre"
                sx={{
                  fontFamily: MONO_FONT,
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  p: 1.5,
                  my: 1.5,
                  borderRadius: '8px',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                  border: `1px solid ${theme.palette.divider}`,
                  overflowX: 'auto',
                  whiteSpace: 'pre',
                }}
              >
                <code>{block.lines.join('\n')}</code>
              </Box>
            );
          case 'table': {
            const header = splitTableRow(block.lines[0]);
            const rows = block.lines.slice(2).map(splitTableRow);
            return (
              <Box key={key} sx={{ overflowX: 'auto', my: 1.5 }}>
                <Table size="small" sx={{ minWidth: 400, '& td, & th': { fontSize: 13 } }}>
                  <TableHead>
                    <TableRow>
                      {header.map((cell, ci) => (
                        <TableCell key={ci} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {renderInline(cell, `${key}-h${ci}`)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, ri) => (
                      <TableRow key={ri}>
                        {header.map((_, ci) => (
                          <TableCell key={ci} sx={{ verticalAlign: 'top' }}>
                            {renderInline(row[ci] ?? '', `${key}-${ri}-${ci}`)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            );
          }
          case 'list':
            return <RenderedList key={key} node={parseList(block.lines)} k={key} />;
          case 'quote':
            return (
              <Box
                key={key}
                sx={{
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  pl: 2,
                  py: 0.5,
                  my: 1.5,
                  color: theme.palette.text.secondary,
                  bgcolor: theme.palette.action.hover,
                  borderRadius: '0 6px 6px 0',
                }}
              >
                {renderInline(block.lines.join(' '), key)}
              </Box>
            );
          case 'hr':
            return <Divider key={key} sx={{ my: 2 }} />;
          case 'paragraph':
          default:
            return (
              <Typography key={key} component="p" sx={{ my: 1, fontSize: 14, lineHeight: 1.7 }}>
                {renderInline(block.lines.join(' '), key)}
              </Typography>
            );
        }
      })}
    </Box>
  );
};

export default HelpMarkdown;
